import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock, Calendar, Sun, Cloud, CloudRain, CloudSnow, Wind, MapPin, Loader2
} from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { MainLayout } from "@/components/layout/MainLayout";

const getWeatherIcon = (code: number | null) => {
  if (!code) return Sun;
  if (code >= 200 && code < 300) return CloudRain;
  if (code >= 300 && code < 600) return CloudRain;
  if (code >= 600 && code < 700) return CloudSnow;
  if (code >= 700 && code < 800) return Wind;
  if (code === 800) return Sun;
  return Cloud;
};

export default function TimeInfo() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useTranslation();

  const [time, setTime] = useState(new Date());
  const [weather, setWeather] = useState<{ temp: number; code: number } | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [loadingWeather, setLoadingWeather] = useState(false);

  const dayKeys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;
  const monthKeys = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"] as const;

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!weather) {
      fetchWeatherByLocation();
    }
  }, [weather]);

  const fetchWeatherByLocation = async () => {
    setLoadingWeather(true);

    if (!navigator.geolocation) {
      setLocationName(null);
      setLoadingWeather(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const weatherRes = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&timezone=auto`
          );
          const weatherData = await weatherRes.json();

          if (weatherData.current) {
            setWeather({
              temp: Math.round(weatherData.current.temperature_2m),
              code: weatherData.current.weather_code,
            });
          }

          const acceptLang = isRTL ? "he" : "en";
          const geoRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=${acceptLang}`
          );
          const geoData = await geoRes.json();

          const city = geoData.address?.city || geoData.address?.town || geoData.address?.village || geoData.address?.municipality;
          setLocationName(city || null);
        } catch {
          setLocationName(null);
        }
        setLoadingWeather(false);
      },
      () => {
        setLocationName(null);
        setLoadingWeather(false);
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  const dayName = t(dayKeys[time.getDay()]);
  const monthName = t(monthKeys[time.getMonth()]);
  const hours = time.getHours().toString().padStart(2, "0");
  const minutes = time.getMinutes().toString().padStart(2, "0");
  const WeatherIcon = getWeatherIcon(weather?.code ?? null);

  const dateString = isRTL 
    ? `${t("day")} ${dayName}, ${time.getDate()} ${monthName} ${time.getFullYear()}`
    : `${dayName}, ${monthName} ${time.getDate()}, ${time.getFullYear()}`;

  return (
    <MainLayout onBack={handleBack}>
      <div className="flex-1 flex flex-col items-center justify-center p-6" dir={dir}>
        <div className="max-w-2xl w-full p-12 rounded-3xl bg-card border-2 border-border space-y-10 text-center">
          <h2 className="text-3xl font-bold text-foreground flex items-center justify-center gap-4">
            <Clock className="w-10 h-10 text-primary" />
            {isRTL ? "שעה ומזג אוויר" : "Time & Weather"}
          </h2>

          {/* Time */}
          <div>
            <span className="text-9xl font-bold text-foreground tracking-wider font-poppins">
              {hours}:{minutes}
            </span>
          </div>

          {/* Date */}
          <div className="flex items-center justify-center gap-4">
            <Calendar className="w-8 h-8 text-primary" />
            <span className="text-3xl text-foreground">{dateString}</span>
          </div>

          {/* Location & Weather */}
          <div className="flex items-center justify-center gap-10 pt-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-7 h-7 text-primary" />
              <span className="text-2xl text-foreground">
                {locationName || t("locationUnavailable")}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {loadingWeather ? (
                <>
                  <Loader2 className="w-7 h-7 text-muted-foreground animate-spin" />
                  <span className="text-2xl text-muted-foreground">{t("loadingWeather")}</span>
                </>
              ) : weather ? (
                <>
                  <WeatherIcon className="w-7 h-7 text-primary" />
                  <span className="text-2xl text-foreground">{weather.temp}°C</span>
                </>
              ) : (
                <>
                  <Sun className="w-7 h-7 text-muted-foreground" />
                  <span className="text-2xl text-muted-foreground">{t("weatherUnavailable")}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
